
import React, { useEffect, useState } from 'react'
import { useTranslation, withTranslation } from 'react-i18next'
import { withRouter, useLocation } from 'react-router-dom'
import { DigitalVaultTrackingView } from '../../views'
import { DocumentLogsService, LocalStorageService } from '../../../services'
import { DateTime } from 'luxon'
import moment from 'moment'
import { parseDate } from '../../../utils/dates.utils'

const DigitalVaultTrackingAction = (props) => {
    const { state } = useLocation()
    const { t } = useTranslation()

    const [documentLogs, setDocumentLogs] = useState([])
    const [documentLogsFiltered, setDocumentLogsFiltered] = useState([])
    const [isLoading, setIsLoading] = useState(true)

    const documentLogsService = new DocumentLogsService(process.env.REACT_APP_MIFIRMA_API)
    const localStorageService = new LocalStorageService()

    let preferredLanguage = localStorageService.getLocalStorageItem('preferredLanguage')
    let language = preferredLanguage || 'es'
    moment.locale(language)

    const [companies, setCompanies] = useState([])
    const [selectedCompanyId, setSelectedCompanyId] = useState('0')
    const [selectedLog, setSelectedLog] = useState(null)
    const [isSearchingBackend, setIsSearchingBackend] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [lastSearchTerm, setLastSearchTerm] = useState('')
    const [currentOffset, setCurrentOffset] = useState(0)
    const [hasMoreData, setHasMoreData] = useState(true)
    const [allLoadedDocuments, setAllLoadedDocuments] = useState([])
    const [autoOpenTriggered, setAutoOpenTriggered] = useState(false)

    const getAllDocumentLogs = async (limit = 1000, offset = 0, searchTerm = null, append = false) => {
        let documentLogs = await documentLogsService.getLogs(null, null, null, limit, offset, searchTerm)
        if (documentLogs.statusCode !== 200) {
            return 0
        }

        documentLogs.data.documentActions = documentLogs.data.documentActions.map(item => {
            if (item.name) item.name = props.t(`digitalVaultStatisticalReportView.key.${item.name}`)
            return item
        })

        let filterOptions = []

        for (let i = 0; i < documentLogs.data.documents.length; i++) {
            const allEvents = [...new Set(documentLogs.data.documents[i].logs
                .map(log => (log?.documentAction?.name)))]
                .filter(eventName => eventName)
                .map((eventName) => {
                    const foundEvent = documentLogs.data.documentActions.find(action => action.name === t(`digitalVaultStatisticalReportView.key.${eventName}`))
                    return ({ id: foundEvent?.id, name: t(`digitalVaultStatisticalReportView.key.${eventName}`) })
                }
                )
            const allUsers = [...new Set(documentLogs.data.documents[i].logs.map(log => (log.user.email)))].map((email) => ({ id: email, name: email }))
            const lastEvent = documentLogs.data.documents[i].logs[0]?.documentAction?.name ? t(`digitalVaultStatisticalReportView.key.${documentLogs.data.documents[i].logs[0]?.documentAction?.name}`) : '';
            const lastUser = documentLogs.data.documents[i].logs[0] && documentLogs.data.documents[i].logs[0]?.user?.email ? documentLogs.data.documents[i].logs[0]?.user?.email : ''

            filterOptions.push({
                id: documentLogs.data.documents[i].id,
                name: documentLogs.data.documents[i].name,
                companyId: documentLogs.data.documents[i].companyId,
                description: documentLogs.data.documents[i].description,
                typeDocument: documentLogs.data.documents[i].typeDocument,
                userName: lastUser,
                startDate: documentLogs.data.documents[i].startDate,
                date: DateTime.fromFormat(documentLogs.data.documents[i].date, 'yyyy-MM-dd HH:mm:ss').toISO(),
                dateValue: DateTime.fromFormat(documentLogs.data.documents[i].date, 'yyyy-MM-dd HH:mm:ss', { locale: language }).toFormat('dd/MMM/yyyy HH:mm:ss'),
                lastEvent,
                logs: documentLogs.data.documents[i].logs,
                allUsers,
                allEvents,
                summary: documentLogs.data.documents[i].summary
            })
        }

        let companyOptions = documentLogs.data.companies
        companyOptions.sort((a, b) => a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase()))
        companyOptions = [{ id: '0', companyName: props.t('digitalVaultTrackingView.combobox.allCompanies') }, ...companyOptions]

        // Only update companies on the initial load
        if (!append) {
            setCompanies(companyOptions)
        }

        setIsLoading(false)

        if (append) {
            const updatedDocs = [...allLoadedDocuments, ...filterOptions]
            setAllLoadedDocuments(updatedDocs)
            // Apply local search if it exists
            if (searchTerm && searchTerm.trim() !== '') {
                const filtered = filterDocumentsByName(updatedDocs, searchTerm)
                setDocumentLogsFiltered(filtered)
            } else {
                setDocumentLogsFiltered(updatedDocs)
            }
        } else {
            setAllLoadedDocuments(filterOptions)
            // Apply local search if it exists
            if (searchTerm && searchTerm.trim() !== '') {
                const filtered = filterDocumentsByName(filterOptions, searchTerm)
                setDocumentLogsFiltered(filtered)
            } else {
                setDocumentLogsFiltered(filterOptions)
            }
        }

        // Only mark hasMoreData as false if we receive 0 documents
        // If we receive less than the limit but more than 0, keep trying
        const receivedCount = documentLogs.data.documents.length
        setHasMoreData(receivedCount > 0)
        setCurrentOffset(offset)

        return receivedCount
    }

    const filterDocumentsByName = (documents, searchTerm) => {
        const needle = searchTerm.toLowerCase().trim()
        return documents.filter(doc => {
            const name = (doc?.name || '').toString().toLowerCase()
            return name.includes(needle)
        })
    }

    useEffect(() => {
        // Limpiar SIEMPRE todo el cache de binnacle al montar el componente
        const clearBinnacleCache = async () => {
            try {
                if ('caches' in window) {
                    const cacheStorage = await caches.open('govo-legal')
                    const allKeys = await cacheStorage.keys()

                    for (const request of allKeys) {
                        if (request.url.includes('/binnacle')) {
                            await cacheStorage.delete(request)
                            console.log('Cache de binnacle eliminado:', request.url)
                        }
                    }
                }
            } catch (error) {
                console.error('Error al limpiar cache de binnacle:', error)
            }
        }

        clearBinnacleCache().then(() => {
            getAllDocumentLogs()
        })

        if (state) {
            setSelectedCompanyId(state.selectedCompanyId)
            setSelectedLog(state.selectedLog)
        }
    }, [])

    // useEffect para detectar documentId en la URL y abrir automáticamente el documento
    useEffect(() => {
        if (autoOpenTriggered || isLoading) {
            return
        }

        const searchParams = new URLSearchParams(window.location.search)
        const hash = window.location.hash || ''
        const queryString = hash.includes('?') ? hash.split('?')[1] : ''
        const hashParams = new URLSearchParams(queryString)

        const documentId = hashParams.get('documentId') || searchParams.get('documentId')

        // Si viene documentId en la URL, abrir automáticamente el documento
        if (documentId) {
            setSelectedLog({ id: documentId })
            setAutoOpenTriggered(true)
        }
    }, [isLoading, autoOpenTriggered])

    const searchInBackend = async (searchTerm) => {
        // Si no hay término de búsqueda, mostrar todos los documentos cargados
        if (!searchTerm || searchTerm.trim() === '') {
            if (lastSearchTerm !== '') {
                // Recargar todos los datos sin filtro
                setLastSearchTerm('')
                setIsSearchingBackend(true)
                try {
                    await getAllDocumentLogs(1000, 0, null, false)
                } catch (error) {
                    console.error('Error al recargar datos:', error)
                } finally {
                    setIsSearchingBackend(false)
                }
            }
            return
        }

        // Si es el mismo término, no hacer nada
        if (lastSearchTerm === searchTerm) {
            return
        }

        setLastSearchTerm(searchTerm)
        setIsSearchingBackend(true)

        try {
            await getAllDocumentLogs(1000, 0, searchTerm, false)
        } catch (error) {
            console.error('Error al buscar en el backend:', error)
        } finally {
            setIsSearchingBackend(false)
        }
    }

    const loadMoreData = async () => {
        if (isLoadingMore || !hasMoreData) {
            return
        }

        setIsLoadingMore(true)

        try {
            const nextOffset = currentOffset + 1000
            // Do NOT pass searchTerm – always load all documents
            await getAllDocumentLogs(1000, nextOffset, lastSearchTerm, true)
        } catch (error) {
            throw new Error('Error al cargar más datos:', error)
        } finally {
            setIsLoadingMore(false)
        }
    }

    const refreshData = async () => {
        try {

            // PRIMERO: Borrar todo el cache de binnacle de forma síncrona
            if ('caches' in window) {
                const cacheStorage = await caches.open('govo-legal')
                const allKeys = await cacheStorage.keys()

                const deletePromises = allKeys
                    .filter(request => request.url.includes('/binnacle'))
                    .map(request => cacheStorage.delete(request))

                await Promise.all(deletePromises)
            }

            // SEGUNDO: Recargar datos frescos del servidor
            await getAllDocumentLogs(1000, 0, lastSearchTerm, false)
        } catch (error) {
            console.error('Error al refrescar datos:', error)
        }
    }

    return (
        <DigitalVaultTrackingView
            isLoading={isLoading}
            documentLogs={documentLogs}
            documentLogsFiltered={documentLogsFiltered}

            companies={companies}
            selectedCompanyId={selectedCompanyId}
            setSelectedCompanyId={setSelectedCompanyId}

            selectedLog={selectedLog}
            setSelectedLog={setSelectedLog}

            searchInBackend={searchInBackend}
            isSearchingBackend={isSearchingBackend}

            loadMoreData={loadMoreData}
            hasMoreData={hasMoreData}
            isLoadingMore={isLoadingMore}

            onRefreshData={refreshData}
        />
    )

}

export default withRouter(withTranslation()(DigitalVaultTrackingAction)) 
